import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

export default function DataTableBlock({ block, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const addRow = () => onChange([...rows, Object.fromEntries(block.columns.map((c) => [c.k, ""]))]);
  const setCell = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-xl border border-border bg-card/60 p-4 print-avoid-break">
      <div className="mb-3 flex items-center justify-between gap-3">
        {block.title && <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">{block.title}</h4>}
        <span className="font-mono text-xs text-muted-foreground">{rows.length} row{rows.length !== 1 ? "s" : ""}</span>
      </div>

      {rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No rows yet.</p>
      ) : (
        <div className="-mx-4 overflow-x-auto px-4">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                {block.columns.map((c) => (
                  <th key={c.k} className="whitespace-nowrap px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">{c.label}</th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-border/50">
                  {block.columns.map((c) => (
                    <td key={c.k} className="px-1 py-1.5 align-top">
                      {c.type === "select" ? (
                        <Select value={row[c.k] || ""} onValueChange={(v) => setCell(i, c.k, v)}>
                          <SelectTrigger data-testid={`table-${block.key}-${i}-${c.k}`} className="h-9 min-w-[120px] bg-secondary/40 text-xs">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            {c.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          data-testid={`table-${block.key}-${i}-${c.k}`}
                          value={row[c.k] || ""}
                          onChange={(e) => setCell(i, c.k, e.target.value)}
                          className="h-9 min-w-[100px] bg-secondary/40 text-xs"
                        />
                      )}
                    </td>
                  ))}
                  <td className="px-1 py-1.5 align-top">
                    <Button
                      data-testid={`table-${block.key}-del-${i}`}
                      variant="ghost" size="icon"
                      onClick={() => delRow(i)}
                      className="h-9 w-9 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Button
        data-testid={`table-${block.key}-add`}
        onClick={addRow}
        variant="outline"
        className="mt-3 gap-2 border-dashed border-primary/40 text-primary tap-scale hover:bg-primary/10"
      >
        <Plus className="h-4 w-4" /> {block.addLabel || "Add row"}
      </Button>
    </div>
  );
}
